import { useState, useCallback, useRef } from 'react';
import alumnosApiService from '../../../../services/http/alumnosApiService';

const PAGE_SIZE = 100;

const fetchPage = async (searchParams, offset) => {
  const requests = [];

  if (searchParams.params) {
    requests.push(
      alumnosApiService.buscarAlumnos({ ...searchParams.params, limit: PAGE_SIZE, offset }),
    );
  }
  if (searchParams.params2) {
    requests.push(
      alumnosApiService.buscarAlumnos({ ...searchParams.params2, limit: PAGE_SIZE, offset }),
    );
  }

  const results = await Promise.all(requests);
  return results.flat();
};

const useSearchAlumnos = () => {
  const [rows, setRows] = useState(null);
  const [isFetching, setIsFetching] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const offsetRef = useRef(0);
  const lastParamsRef = useRef(null);

  const search = useCallback(async (searchParams) => {
    if (!searchParams) return;
    setIsFetching(true);
    offsetRef.current = 0;

    try {
      const data = await fetchPage(searchParams, 0);
      setRows(data);
      setHasMore(data.length >= PAGE_SIZE);
      offsetRef.current = data.length;
      lastParamsRef.current = searchParams;
    } catch (_err) {
      setRows([]);
      setHasMore(false);
    } finally {
      setIsFetching(false);
    }
  }, []);

  const loadMore = useCallback(async () => {
    if (!lastParamsRef.current || isLoadingMore || !hasMore) return;
    setIsLoadingMore(true);

    try {
      const data = await fetchPage(lastParamsRef.current, offsetRef.current);
      if (data.length > 0) {
        setRows((prev) => [...(prev || []), ...data]);
        offsetRef.current += data.length;
      }
      setHasMore(data.length >= PAGE_SIZE);
    } catch (_err) {
      setHasMore(false);
    } finally {
      setIsLoadingMore(false);
    }
  }, [isLoadingMore, hasMore]);

  return {
    data: rows ? { rows, total: rows.length, hasMore } : null,
    isFetching,
    isLoadingMore,
    hasMore,
    search,
    loadMore,
  };
};

export default useSearchAlumnos;
